import { Link } from 'react-router-dom';
import type { PrimaryAtmosphere, Amenity } from '@wineryxplorer/shared';
import { AmenitiesDisplay } from './VenueDisplay';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '@wineryxplorer/shared';
import { ImgWithSkeleton } from './ui/ImgWithSkeleton';

interface VenueCardProps {
  venue: {
    _id: string;
    name: string;
    status?: 'draft' | 'published';
    address?: {
      city?: string;
      state?: string;
    };
    priceRange?: number | null;
    primaryAtmosphere?: PrimaryAtmosphere | null;
    amenities?: Amenity[];
    logoImageId?: string | null;
  };
  logoMedia?: { cloudinaryUrl: string } | null;
}

// Visual indicator components for compact display.
// Price + atmosphere are quiet metadata — they let the $ glyph count and
// the emoji carry character. No saturated pill colors.
const PriceRangeIndicator = ({ value }: { value?: number | null }) => {
  if (!value) return null;

  const symbols = '$'.repeat(value);

  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold tracking-wider bg-gray-100 text-gray-800 border border-gray-200">
      {symbols}
    </span>
  );
};

const AtmosphereIndicator = ({ value }: { value?: PrimaryAtmosphere | null }) => {
  if (!value) return null;

  const icons: Record<PrimaryAtmosphere, string> = {
    casual: '😊',
    upscale: '✨',
    rustic: '🏡',
    modern: '🏢',
  };

  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
      <span className="mr-1">{icons[value]}</span>
      {value.charAt(0).toUpperCase() + value.slice(1)}
    </span>
  );
};

const AmenitiesIndicator = ({ amenities }: { amenities?: Amenity[] }) => {
  if (!amenities || amenities.length === 0) return null;
  
  const iconMap: Record<Amenity, string> = {
    'scenic-views': '🏞️',
    'historic-building': '🏛️',
    'family-friendly': '👨‍👩‍👧‍👦',
    'pet-friendly': '🐕',
    'outdoor-seating': '🌳',
    'live-music': '🎵',
    'food-service': '🍽️',
    'tours-available': '🚶‍♂️',
    'event-space': '🎉',
    'wheelchair-accessible': '♿',
  };
  
  const displayAmenities = amenities.slice(0, 3);
  const remainingCount = amenities.length - 3;
  
  return (
    <div className="flex items-center space-x-1">
      {displayAmenities.map((amenity) => (
        <span
          key={amenity}
          className="text-sm"
          title={amenity.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        >
          {iconMap[amenity]}
        </span>
      ))}
      {remainingCount > 0 && (
        <span className="text-xs text-gray-500 font-medium">
          +{remainingCount}
        </span>
      )}
    </div>
  );
};

export const VenueCard = ({ venue, logoMedia }: VenueCardProps) => {
  const { user } = useAuth();

  // Check if user is admin
  const isAdmin = user && (user.role === UserRole.SITE_ADMIN || user.role === UserRole.SUPER_ADMIN);
  const isDraft = venue.status === 'draft';

  return (
    <li className="border-b border-gray-200 py-4">
      <Link 
        to={`/wineries/${venue._id}`}
        className="block hover:bg-gray-50 active:bg-gray-100 -mx-2 px-3 py-3 rounded-lg transition-colors touch-manipulation"
        style={{ minHeight: '44px' }}
      >
        <div className="flex flex-col space-y-3">
          {/* Header with name, logo, and location */}
          <div className="flex flex-col">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-xl sm:text-2xl font-normal leading-tight text-gray-900 hover:text-primary-700 mb-1">
                  {venue.name}
                </h3>
                {venue.address?.city && venue.address.state && (
                  <span className="text-gray-600 text-sm sm:text-base">
                    {venue.address.city}, {venue.address.state}
                  </span>
                )}
              </div>
              
              {/* Logo - right side, top aligned, max 200px */}
              <div className="ml-4 flex-shrink-0 border border-gray-100 p-1">
                <ImgWithSkeleton
                  src={venue.logoImageId && logoMedia ? logoMedia.cloudinaryUrl : '/assets/placeholders/placeholder-winery-logo.png'}
                  alt={`${venue.name} logo`}
                  fallbackSrc="/assets/placeholders/placeholder-winery-logo.png"
                  className="h-12 w-auto max-w-[200px] object-contain"
                  skeletonClassName="rounded"
                />
              </div>
            </div>
            
            {/* Price range and atmosphere badges under location, left-aligned */}
            <div className="flex items-center space-x-2 mt-2">
              {isDraft && isAdmin && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium tracking-widest bg-amber-50 text-amber-900 border border-amber-200">
                  DRAFT
                </span>
              )}
              <PriceRangeIndicator value={venue.priceRange} />
              <AtmosphereIndicator value={venue.primaryAtmosphere} />
            </div>
          </div>
          
          {/* Amenities row */}
          {venue.amenities && venue.amenities.length > 0 && (
            <div className="flex items-center justify-between">
              <AmenitiesIndicator amenities={venue.amenities} />
              
              {/* Full amenities display for detailed view */}
              <div className="hidden sm:block">
                <AmenitiesDisplay 
                  value={venue.amenities} 
                  maxDisplay={2} 
                  className="flex-wrap"
                />
              </div>
            </div>
          )}
          

        </div>
      </Link>
    </li>
  );
};